import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import { motion } from 'framer-motion';
import { useAtom } from 'jotai';

import { MAX_PROGRESS, progressAtom } from '@store';
import { ScrubberHandle, useResizeObserver } from '@ui';

import * as css from './ProgressBar.css';

const DEFAULT_SCRUBBER_WIDTH = 1310;
const DEFAULT_SCRUBBER_HANDLE = 50;
export const X_OFFSET = 25;

type ProgressBarProps = PropsWithChildren<{
    onScrubStart?: () => void;
    variant?: 'default' | 'thin';
}>;

export const ProgressBar = ({ children, onScrubStart, variant = 'default' }: ProgressBarProps) => {
    const [progress, setProgress] = useAtom(progressAtom);
    const ref = useRef<HTMLDivElement>(null);
    const { width } = useResizeObserver({ ref });

    const progressRatio = progress / 100;
    // Full element width with a fallback.
    const elementWidth = width ?? DEFAULT_SCRUBBER_WIDTH;
    // inner width (from scrollable locations)
    const maxWidth = elementWidth - X_OFFSET * 2;
    const xProgress = X_OFFSET + progressRatio * maxWidth;

    const [isPointerDown, setIsPointerDown] = useState(false);

    const mousePositionRef = useRef(0);

    const handlePointerDown: React.MouseEventHandler<HTMLDivElement> = useCallback(
        (e) => {
            e.preventDefault();
            const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            const x = e.clientX - rect.left - X_OFFSET;
            const clampedX = Math.max(0, Math.min(x, maxWidth));
            const clickProgress = Math.min((clampedX / maxWidth) * MAX_PROGRESS, MAX_PROGRESS);
            setProgress(clickProgress);
            onScrubStart?.();
            setIsPointerDown(true);
        },
        [maxWidth, onScrubStart, setProgress],
    );

    useEffect(() => {
        if (!isPointerDown || !ref.current) {
            return;
        }

        const rect = ref.current.getBoundingClientRect();

        const pointerMoveHandler = (e: MouseEvent) => {
            const x = e.clientX - rect.left - X_OFFSET;
            const clampedX = Math.max(0, Math.min(x, maxWidth));
            // const progress = (clampedX / maxWidth) * 100;
            // mousePositionRef.current += e.movementX;
            // const scrubX = Math.max(mousePositionRef.current, 0);
            const scrubProgress = Math.min((clampedX / maxWidth) * MAX_PROGRESS, MAX_PROGRESS);
            setProgress(scrubProgress);
        };
        const pointerStopHandler = () => {
            mousePositionRef.current = 0;
            setIsPointerDown(false);
        };

        window.addEventListener('pointermove', pointerMoveHandler);
        window.addEventListener('pointerup', pointerStopHandler);
        window.addEventListener('blur', pointerStopHandler);

        return () => {
            window.removeEventListener('pointermove', pointerMoveHandler);
            window.removeEventListener('pointerup', pointerStopHandler);
            window.removeEventListener('blur', pointerStopHandler);
        };
    }, [elementWidth, isPointerDown, maxWidth, setProgress]);

    return (
        <motion.div className={css.positioner[variant]}>
            <div className={css.scrubber} ref={ref} onPointerDown={handlePointerDown}>
                <div className={css.scrubberContent[variant]}>{children}</div>
                {variant === 'default' && (
                    <motion.div
                        className={css.handle}
                        drag="x"
                        dragElastic={false}
                        dragMomentum={false}
                        dragDirectionLock
                        style={{ x: xProgress - DEFAULT_SCRUBBER_HANDLE / 2 }}
                    >
                        <ScrubberHandle />
                    </motion.div>
                )}
            </div>
            <div className={css.progressMask[variant]}>
                <motion.div
                    className={css.progress[variant]}
                    // Exceptions on the edges: when progress === 0, we want 0; when progress === 100, we want 1.
                    style={{ scaleX: progress === 0 || progress === 100 ? progressRatio : xProgress / elementWidth }}
                />
            </div>
        </motion.div>
    );
};
